import {
  BOOKKEEPING_BUCKET_LABELS,
  type BookkeepingBucket,
  type BookkeepingRecord,
  bucketFor,
  formatMinor,
  TRANSACTION_STATUS_LABELS,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, cn, Spinner } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  bookkeepingKeys,
  bookkeepingListQuery,
  bookkeepingStatsQuery,
  confirmBookkeepingRecord,
} from '../../shared/api/bookkeeping-queries.js';
import { BulkBar } from './bulk-bar.js';

/**
 * The bookkeeping queue.
 *
 * A record arrives here already built, supplier matched, category proposed,
 * HST separated, so the work is confirming and correcting rather than typing.
 * The buckets across the top are the shape of a bookkeeper's morning: deal with
 * what is missing, then confirm the rest.
 *
 * Confirming exports nothing. QuickBooks is the system of record and this
 * screen never writes to it.
 */

const BUCKETS: readonly BookkeepingBucket[] = [
  'needs_vendor',
  'needs_categorization',
  'needs_tax_review',
  'needs_job',
  'ready_for_export',
  'exported',
];

export function BookkeepingPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const [bucket, setBucketState] = useState<BookkeepingBucket | undefined>(undefined);
  const [search, setSearchState] = useState('');
  const [error, setError] = useState<string | null>(null);

  const records = useQuery(
    bookkeepingListQuery(organizationId, {
      ...(bucket === undefined ? {} : { bucket }),
      ...(search.trim() === '' ? {} : { query: search.trim() }),
    }),
  );
  const stats = useQuery(bookkeepingStatsQuery(organizationId));

  const confirm = useMutation({
    mutationFn: (recordId: string) => confirmBookkeepingRecord(organizationId, recordId),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: bookkeepingKeys.all(organizationId) });
    },
    // The database refuses a record that still needs a decision, and it says
    // which one. Surfaced verbatim rather than replaced with a generic message.
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That record could not be confirmed.'),
  });

  const items = records.data?.items ?? [];
  // A new list starts with nothing selected and the cursor at the top.
  const setBucket = (next: BookkeepingBucket | undefined) => {
    setBucketState(next);
    setSelected(new Set());
    setCursor(0);
  };
  const setSearch = (next: string) => {
    setSearchState(next);
    setSelected(new Set());
    setCursor(0);
  };
  const drafts = items.filter((record) => record.status === 'draft');

  // Selection and a cursor, for clearing a run of drafts from the keyboard.
  const navigate = useNavigate();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [cursor, setCursor] = useState(0);
  const [showKeys, setShowKeys] = useState(false);
  const toggle = (id: string) =>
    setSelected((before) => {
      const next = new Set(before);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectedDrafts = drafts.filter((record) => selected.has(record.id));

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        if (event.key === 'Escape') target.blur();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const row = items[cursor];
      switch (event.key) {
        case 'j':
        case 'ArrowDown':
          event.preventDefault();
          setCursor((at) => Math.min(at + 1, Math.max(items.length - 1, 0)));
          break;
        case 'k':
        case 'ArrowUp':
          event.preventDefault();
          setCursor((at) => Math.max(at - 1, 0));
          break;
        case 'x':
          if (row?.status === 'draft') {
            event.preventDefault();
            toggle(row.id);
          }
          break;
        case 'X':
          event.preventDefault();
          setSelected((before) =>
            before.size === drafts.length ? new Set() : new Set(drafts.map((record) => record.id)),
          );
          break;
        case 'Enter':
        case 'o':
          if (row !== undefined) {
            event.preventDefault();
            void navigate({ to: '/bookkeeping/$recordId', params: { recordId: row.id } });
          }
          break;
        case 'c':
          if (row?.status === 'draft') {
            event.preventDefault();
            confirm.mutate(row.id);
          }
          break;
        case 'Escape':
          setSelected(new Set());
          setShowKeys(false);
          break;
        case '?':
          setShowKeys((before) => !before);
          break;
        case '/':
          event.preventDefault();
          document.getElementById('bookkeeping-search')?.focus();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">Bookkeeping</h1>
        <p className="mt-1 text-sm text-content-muted">
          Approved documents, turned into records. Nothing here is sent to QuickBooks.
        </p>
      </div>

      {stats.data === undefined ? null : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Draft" value={stats.data.draft} />
          <Stat label="Needs a supplier" value={stats.data.needsVendor} tone="warning" />
          <Stat label="Needs a category" value={stats.data.needsCategorization} tone="warning" />
          <Stat label="Ready for export" value={stats.data.readyForExport} tone="success" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={bucket === undefined ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setBucket(undefined)}
        >
          Everything
        </Button>
        {BUCKETS.map((candidate) => (
          <Button
            key={candidate}
            variant={bucket === candidate ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setBucket(candidate)}
          >
            {BOOKKEEPING_BUCKET_LABELS[candidate]}
          </Button>
        ))}
      </div>

      <input
        id="bookkeeping-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by supplier, reference or file name"
        aria-label="Search bookkeeping records"
        className="rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
      />

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      {showKeys ? (
        <Card>
          <CardBody>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
              {KEYS.map(([keys, what]) => (
                <div key={keys} className="flex items-baseline gap-2">
                  <dt>
                    <kbd className="rounded border border-border bg-surface-sunken px-1.5 py-0.5 font-mono text-xs">
                      {keys}
                    </kbd>
                  </dt>
                  <dd className="text-content-muted">{what}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      ) : null}

      {selectedDrafts.length === 0 ? null : (
        <BulkBar
          organizationId={organizationId}
          records={selectedDrafts}
          onDone={() => setSelected(new Set())}
          onClear={() => setSelected(new Set())}
        />
      )}

      {records.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-6 text-brand-600" label="Loading records" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardBody className="py-12 text-center">
            <p className="text-sm text-content-muted">
              Nothing here. Records appear as documents are approved in review.
            </p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={`${items.length} record${items.length === 1 ? '' : 's'}`}
            description="Tick drafts to code or confirm them together. Press ? for keys."
          />
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-content-subtle">
                    <th className="w-8 px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label="Select every draft shown"
                        checked={drafts.length > 0 && selectedDrafts.length === drafts.length}
                        disabled={drafts.length === 0}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked
                              ? new Set(drafts.map((record) => record.id))
                              : new Set(),
                          )
                        }
                      />
                    </th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold">Supplier</th>
                    <th className="px-3 py-2 font-semibold">Category</th>
                    <th className="px-3 py-2 text-right font-semibold">Net</th>
                    <th className="px-3 py-2 text-right font-semibold">HST</th>
                    <th className="px-3 py-2 text-right font-semibold">Total</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((record, index) => (
                    <RecordRow
                      key={record.id}
                      record={record}
                      isCursor={index === cursor}
                      isSelected={selected.has(record.id)}
                      onToggle={() => toggle(record.id)}
                      onConfirm={() => confirm.mutate(record.id)}
                      isConfirming={confirm.isPending && confirm.variables === record.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'success';
}) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface px-3 py-2">
      <p className="text-xs text-content-muted">{label}</p>
      <p
        className={cn(
          'tabular text-lg font-semibold',
          value === 0
            ? 'text-content-subtle'
            : tone === 'warning'
              ? 'text-warning'
              : tone === 'success'
                ? 'text-success'
                : 'text-content',
        )}
      >
        {value}
      </p>
    </div>
  );
}

const KEYS: readonly (readonly [string, string])[] = [
  ['j / k', 'Next / previous'],
  ['x', 'Select this draft'],
  ['X', 'Select every draft'],
  ['o / Enter', 'Open'],
  ['c', 'Confirm this one'],
  ['/', 'Search'],
  ['Esc', 'Clear the selection'],
  ['?', 'These keys'],
];

function RecordRow({
  record,
  onConfirm,
  isConfirming,
  isCursor,
  isSelected,
  onToggle,
}: {
  record: BookkeepingRecord;
  onConfirm: () => void;
  isConfirming: boolean;
  isCursor: boolean;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const bucket = bucketFor(record);
  const outstanding = record.openReasons.length > 0;

  return (
    <tr
      className={cn(
        'border-b border-border last:border-0 align-top',
        isSelected ? 'bg-brand-50/60' : '',
        isCursor ? 'outline outline-2 -outline-offset-2 outline-brand-400' : '',
      )}
      ref={(row) => {
        if (isCursor) row?.scrollIntoView({ block: 'nearest' });
      }}
    >
      <td className="px-3 py-2">
        {record.status === 'draft' ? (
          <input
            type="checkbox"
            aria-label="Select this record"
            checked={isSelected}
            onChange={onToggle}
          />
        ) : null}
      </td>
      <td className="tabular px-3 py-2 whitespace-nowrap">{record.transactionDate}</td>
      <td className="px-3 py-2">
        <Link
          to="/bookkeeping/$recordId"
          params={{ recordId: record.id }}
          className="text-brand-600 hover:underline"
        >
          {record.vendorName ?? <span className="text-warning">Not matched</span>}
        </Link>
        {record.documentFilename === null ? null : (
          <span className="block truncate text-xs text-content-subtle">
            {record.documentFilename}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        {record.categories ?? <span className="text-warning">Uncategorised</span>}
        {record.hasCapitalLine === true ? (
          <span className="ml-1 rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
            Capital
          </span>
        ) : null}
      </td>
      <td className="tabular px-3 py-2 text-right">{formatMinor(record.netAmountMinor)}</td>
      <td className="tabular px-3 py-2 text-right">{formatMinor(record.taxAmountMinor)}</td>
      <td className="tabular px-3 py-2 text-right font-medium">
        {formatMinor(record.grossAmountMinor)}
      </td>
      <td className="px-3 py-2">
        <span className="text-xs text-content-muted">
          {TRANSACTION_STATUS_LABELS[record.status]}
        </span>
        {outstanding ? (
          <span className="block text-xs text-warning">{BOOKKEEPING_BUCKET_LABELS[bucket]}</span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right">
        {record.status === 'draft' ? (
          <Button
            size="sm"
            variant={outstanding ? 'ghost' : 'secondary'}
            onClick={onConfirm}
            disabled={isConfirming}
            // Left enabled even with work outstanding: the database explains
            // exactly what is missing, which is more useful than a dead button.
            title={outstanding ? 'Some details still need a decision' : undefined}
          >
            {isConfirming ? 'Confirming…' : 'Confirm'}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}
